import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Eye, EyeSlash, SignIn, ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@example.com");
  const [password, setPassword] = useState("admin123");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user && user.role) navigate(homeForRole(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.full_name}`);
      navigate(homeForRole(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Toaster position="top-right" richColors />
    <div className="min-h-screen md:h-screen grid md:grid-cols-2 bg-zinc-50 overflow-hidden">

      {/* Left — form */}
      <div className="flex flex-col p-8 md:p-12 justify-between md:overflow-y-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-indigo-950 text-white grid place-items-center font-display font-bold text-lg">IR</div>
          <div>
            <div className="font-display font-semibold text-zinc-900">Wall to Wall</div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-[0.14em]">Warehouse Inventory</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto py-10">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">Sign in</h1>
          <p className="text-sm text-zinc-600 mt-2">Access your session as Admin, Counter, or Head Counter.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 mb-1.5">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 pr-10 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPass((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-zinc-500 hover:text-zinc-900"
                  data-testid="toggle-password-visibility">
                  {showPass ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-60 text-white px-4 py-3 font-medium transition-colors"
              data-testid="login-submit-button"
            >
              <SignIn className="w-4 h-4" />
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 rounded-sm border border-zinc-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-zinc-700 uppercase tracking-wider"><ShieldCheck className="w-4 h-4 text-indigo-900" /> Demo credentials</div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono">
              <div className="rounded-sm border border-zinc-200 p-2"><div className="text-zinc-500 font-sans uppercase tracking-wider text-[10px]">Admin</div>admin@example.com<br/>admin123</div>
              <div className="rounded-sm border border-zinc-200 p-2"><div className="text-zinc-500 font-sans uppercase tracking-wider text-[10px]">Counter</div>gen@example.com<br/>counter123</div>
              <div className="rounded-sm border border-zinc-200 p-2"><div className="text-zinc-500 font-sans uppercase tracking-wider text-[10px]">Head Counter</div>headcounter@example.com<br/>headcounter123</div>
            </div>
          </div>
        </div>

        <div className="text-[11px] text-zinc-500">© {new Date().getFullYear()} Warehouse Inventory System</div>
      </div>

      {/* Right — hero image */}
      <div className="relative hidden md:block overflow-hidden h-full">
        <div
          className="absolute inset-0 bg-cover bg-center grayscale"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/4487365/pexels-photo-4487365.jpeg)" }}
        />
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="relative z-10 h-full flex flex-col justify-end p-10 text-white">
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 rounded-sm border border-white/30 px-2 py-1 text-[10px] uppercase tracking-[0.2em]">Wall to Wall · Inventory</div>
            <h2 className="font-display text-3xl font-semibold mt-4 leading-tight">Dwellstone Inc.</h2>
            <p className="text-sm text-white/80 mt-3">Dwellstone | Mozzaico | Vivari</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
